import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { enablePush, getPushState, type PushState } from "@/lib/push";
import { projectTone } from "@/design/project-visual";
import { toneColor } from "@/design/primitives";

/** Mobilní PŘEHLED (spec §74): attention overview + plán + digest. */
export function MobileHome() {
  const navigate = useNavigate();
  const projectsQuery = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const inbox = useQuery({ queryKey: ["inbox"], queryFn: () => api.decisionInbox() });
  const incidentsQuery = useQuery({ queryKey: ["incidents"], queryFn: api.incidents });
  const workersQuery = useQuery({ queryKey: ["workers"], queryFn: api.workers, refetchInterval: 30_000 });
  const planQuery = useQuery({ queryKey: ["plan"], queryFn: api.plan });
  const auditToday = useQuery({ queryKey: ["audit", "today"], queryFn: () => api.audit({ limit: 200 }) });

  const projects = (projectsQuery.data?.projects ?? [])
    .filter((p) => p.lifecycle !== "ARCHIVED")
    .slice(0, 6);
  const decisionCount = (inbox.data?.items ?? []).length;
  const incidents = incidentsQuery.data?.incidents ?? [];
  const liveSessions = (workersQuery.data?.workers ?? []).filter((w) => w.activeSession);

  const [bannerDismissed, setBannerDismissed] = useState(false);
  /** Co stojí: urgentní rozhodnutí, které drží projekt, jinak kritický incident. */
  const blocker = useMemo(() => {
    const items = inbox.data?.items ?? [];
    const blocking = items.find((i) => i.decision?.blocksProject) ?? items.find((i) => i.urgency === "URGENT");
    if (blocking) return cs.mobile.blockerDecision(blocking.projectName ?? "Factory");
    const critical = incidents.find((i) => i.severity === "P0" || i.severity === "P1");
    return critical ? cs.mobile.blockerIncident(critical.projectName, critical.label) : null;
  }, [inbox.data, incidents]);

  const digest = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const events = (auditToday.data?.events ?? []).filter((e) => new Date(e.ts) >= start);
    const done = events.filter((e) => e.action === "QA_PASSED" || e.action === "TASK_STATUS_CHANGED").length;
    const highlights = events
      .filter((e) => ["QA_PASSED", "INCIDENT_CREATED", "DEPLOY_STAGING", "PROPOSAL_CREATED", "HEALTH_ALERT_CREATED"].includes(e.action))
      .slice(0, 4);
    return { done, highlights };
  }, [auditToday.data]);

  return (
    <div className="af-animate-fade" data-testid="mobile-home">
      {/* banner kritických věcí nahoře (p1d) — na telefonu se scrolluje málo,
          takže to, co stojí, musí být vidět dřív než cokoli jiného */}
      {blocker && !bannerDismissed ? (
        <div
          className="mb-3 flex items-center gap-2.5 px-3.5 py-2.5"
          style={{
            borderRadius: "var(--radius-control)",
            border: "1px solid color-mix(in srgb, var(--critical) 45%, transparent)",
            background: "var(--critical-subtle)",
          }}
          data-testid="mobile-critical-banner"
        >
          <span style={{ color: "var(--critical)" }}>⛔</span>
          <Link to="/decisions" className="min-w-0 flex-1 text-[13px]" style={{ color: "var(--text-primary)" }}>
            {blocker}
          </Link>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center text-[14px]"
            style={{ color: "var(--text-tertiary)" }}
            aria-label={cs.common.close}
            onClick={() => setBannerDismissed(true)}
          >
            ✕
          </button>
        </div>
      ) : null}

      {/* hero */}
      <div className="af-display mx-1 mb-3.5 mt-1 text-[17px] font-light leading-relaxed" style={{ color: "var(--text-primary)" }}>
        {cs.mobile.heroRunning}{" "}
        {decisionCount > 0 || incidents.length > 0 ? (
          <>
            <span style={{ color: "var(--text-secondary)" }}>{cs.mobile.heroWaiting} </span>
            <Link to="/decisions" style={{ color: "var(--warning)" }}>
              {cs.mobile.heroDecisions(decisionCount)}
            </Link>
            {incidents.length > 0 ? (
              <>
                <span style={{ color: "var(--text-secondary)" }}>{cs.mobile.heroWatching} </span>
                <Link to="/incidents/$id" params={{ id: incidents[0]!.id }} style={{ color: "var(--critical)" }}>
                  {cs.mobile.heroIncidents(incidents.length)}
                </Link>
              </>
            ) : null}
            <span style={{ color: "var(--text-secondary)" }}>.</span>
          </>
        ) : (
          <span style={{ color: "var(--text-secondary)" }}>{cs.mobile.heroCalm}</span>
        )}
      </div>

      {/* KPI */}
      <div className="mb-3.5 grid grid-cols-3 gap-2">
        <Kpi value={liveSessions.length} label={cs.mobile.kpiSessions} color="var(--positive)" border="color-mix(in srgb, var(--accent) 14%, transparent)" onClick={() => void navigate({ to: "/workers" })} />
        <Kpi value={decisionCount} label={cs.mobile.kpiDecisions} color="var(--warning)" border="color-mix(in srgb, var(--warning) 30%, transparent)" onClick={() => void navigate({ to: "/decisions" })} />
        <Kpi
          value={incidents.length}
          label={cs.mobile.kpiIncidents}
          color={incidents.length ? "var(--critical)" : "var(--text-secondary)"}
          border={incidents.length ? "color-mix(in srgb, var(--critical) 35%, transparent)" : "color-mix(in srgb, var(--accent) 14%, transparent)"}
          onClick={incidents.length ? () => void navigate({ to: "/incidents/$id", params: { id: incidents[0]!.id } }) : undefined}
        />
      </div>

      <PushOptIn />

      {/* projekty dle attention */}
      <div className="af-label-dim mx-1 mb-2">{cs.mobile.projects}</div>
      <div className="mb-3.5 flex flex-col gap-1.5" data-testid="mobile-projects">
        {projects.map((p) => {
          const tone = projectTone(p);
          const critical = tone === "red";
          return (
            <Link
              key={p.id}
              to="/projects/$slug"
              params={{ slug: p.slug }}
              className="flex items-center gap-2.5 px-3 py-2.5"
              style={{ borderRadius: "var(--radius-box)",
                border: `1px solid ${critical ? "color-mix(in srgb, var(--critical) 35%, transparent)" : tone === "amber" ? "color-mix(in srgb, var(--warning) 30%, transparent)" : "color-mix(in srgb, var(--accent) 10%, transparent)"}`,
                background: critical ? "color-mix(in srgb, var(--critical) 4%, transparent)" : "transparent",
              }}
            >
              <span
                className={critical ? "af-animate-pulse h-2 w-2 flex-none rounded-full" : "h-2 w-2 flex-none rounded-full"}
                style={{ background: toneColor[tone], boxShadow: critical ? "0 0 8px color-mix(in srgb, var(--critical) 80%, transparent)" : "none" }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-bold" style={{ color: "var(--text-primary)" }}>
                  {p.name}
                </span>
                <span className="block truncate text-[11px]" style={{ color: critical ? "var(--critical)" : tone === "amber" ? "var(--warning)" : "var(--text-secondary)" }}>
                  {p.statusLine ?? p.lifecycle}
                </span>
              </span>
              <span className="text-[12px] font-bold" style={{ color: toneColor[tone] }}>
                {p.attentionScore}
              </span>
            </Link>
          );
        })}
      </div>

      {/* execution plan */}
      <div className="af-label-dim mx-1 mb-2">{cs.mobile.plan}</div>
      <div className="mb-3.5 px-3 py-2.5 text-[11px] leading-[1.9]" style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--text-secondary)" }}>
        {liveSessions.length === 0 ? (
          <span style={{ color: "var(--text-tertiary)" }}>{cs.mobile.planIdle}</span>
        ) : (
          liveSessions.map((w) => {
            const s = w.activeSession!;
            return (
              <div key={w.id}>
                <span style={{ color: "var(--positive)" }}>●</span> {w.name} → {s.projectName} #{s.taskNumber} {s.progress} %
              </div>
            );
          })
        )}
        {(planQuery.data?.next ?? []).slice(0, 2).map((t) => (
          <div key={t.id} style={{ color: "var(--text-tertiary)" }}>
            {cs.mobile.planNext} #{t.number} {t.title}
          </div>
        ))}
      </div>

      {/* digest */}
      <div className="af-label-dim mx-1 mb-2">{cs.mobile.digest}</div>
      <div className="px-3.5 py-3 text-[11px] leading-[1.9]" style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--text-secondary)" }} data-testid="mobile-digest">
        <div>
          <span style={{ color: "var(--positive)" }}>✓</span> {cs.mobile.digestDone(digest.done)}
        </div>
        {digest.highlights.map((e) => (
          <div key={e.id}>
            <span style={{ color: e.action === "INCIDENT_CREATED" || e.action === "HEALTH_ALERT_CREATED" ? "var(--critical)" : "var(--positive)" }}>
              {e.action === "INCIDENT_CREATED" || e.action === "HEALTH_ALERT_CREATED" ? "●" : "✓"}
            </span>{" "}
            {e.projectName ? `${e.projectName}: ` : ""}
            {e.summary}
          </div>
        ))}
        {digest.done === 0 && digest.highlights.length === 0 ? (
          <span style={{ color: "var(--text-tertiary)" }}>{cs.mobile.digestNothing}</span>
        ) : null}
      </div>
    </div>
  );
}

function Kpi({ value, label, color, border, onClick }: { value: number; label: string; color: string; border: string; onClick?: () => void }) {
  return (
    <button type="button" className="cursor-pointer px-2 py-2.5 text-center" style={{ borderRadius: "var(--radius-box)", border: `1px solid ${border}` }} onClick={onClick}>
      <div className="af-card-title" style={{ color }}>
        {value}
      </div>
      <div className="text-[11px] tracking-[0.14em]" style={{ color: "var(--text-tertiary)" }}>
        {label}
      </div>
    </button>
  );
}

/** Opt-in na push notifikace — jen když jsou na serveru nakonfigurované. */
function PushOptIn() {
  const [state, setState] = useState<PushState | "loading">("loading");
  useState(() => {
    void getPushState().then(setState);
    return undefined;
  });
  if (state === "loading" || state === "unsupported" || state === "unavailable" || state === "denied") return null;
  if (state === "subscribed") {
    return (
      <div className="mb-3 text-center text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.mobile.notificationsOn}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="mb-3.5 w-full cursor-pointer py-2.5 text-[11px] tracking-[0.1em]"
      style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)", color: "var(--accent)" }}
      onClick={() => void enablePush().then((ok) => setState(ok ? "subscribed" : "ready"))}
      data-testid="mobile-enable-push"
    >
      {cs.mobile.enableNotifications}
    </button>
  );
}
